/** Known test / seed records to exclude from partner-facing reports. */
const TEST_EMAIL_PATTERNS = [/john\.doe@halalfoods\.com/i];

export const MIN_REPORT_LOAN_AMOUNT = 1000;

/** City-level LMI proxy (not census-tract grade — for pipeline visualization only). */
const LMI_PROXY_BY_CITY = {
  'hamtramck, michigan': 'likely_lmi',
  'bronx, new york': 'likely_lmi',
  'milwaukee, wisconsin': 'likely_lmi',
  'spokane, washington': 'mixed',
  'seattle, washington': 'mixed',
  'cincinnati, ohio': 'mixed',
  'lawrenceburg, tennessee': 'mixed',
  'morganville, new jersey': 'mixed',
  'franklin square, new york': 'mixed',
  'sammamish, washington': 'unlikely_lmi',
  'bellevue, washington': 'unlikely_lmi',
  'redmond, washington': 'unlikely_lmi',
  'bothell, washington': 'unlikely_lmi',
  'mill creek, washington': 'unlikely_lmi',
  'mckinney, texas': 'unlikely_lmi',
  'dallas, texas': 'mixed',
  'irving, texas': 'mixed',
  'san jose, california': 'mixed',
};

const STATE_ALIASES = {
  ny: 'New York',
  wa: 'Washington',
  ca: 'California',
  tx: 'Texas',
  nj: 'New Jersey',
  wi: 'Wisconsin',
  wy: 'Wyoming',
  ga: 'Georgia',
  mi: 'Michigan',
  oh: 'Ohio',
  tn: 'Tennessee',
  az: 'Arizona',
};

export function parseLoanAmount(raw) {
  if (raw === undefined || raw === null || raw === '') return null;
  const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(n) || n <= 0 ? null : n;
}

export function normalizeState(state) {
  const s = String(state || '').trim();
  if (!s) return '';
  const key = s.toLowerCase();
  if (STATE_ALIASES[key]) return STATE_ALIASES[key];
  if (s.length === 2) {
    const full = STATE_ALIASES[key];
    if (full) return full;
  }
  return s.replace(/\s+/g, ' ');
}

export function normalizeCity(city) {
  return String(city || '')
    .trim()
    .replace(/,+$/, '')
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

export function cityStateKey(city, state) {
  return `${normalizeCity(city).toLowerCase()}, ${normalizeState(state).toLowerCase()}`;
}

export function isTestRecord(record) {
  const email = String(record?.email || '').trim();
  return TEST_EMAIL_PATTERNS.some((re) => re.test(email));
}

function normalizeNameKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9\s&'-]/g, '')
    .replace(/\s+/g, ' ');
}

/** Dedupe key: same email + business (or contact when business missing). */
export function buildDedupeKey(record) {
  const email = normalizeNameKey(record.email);
  const business = normalizeNameKey(record.businessName);
  const contact = normalizeNameKey(record.contactName);
  const namePart =
    business && business !== '' && business !== '—' ? business : contact;
  if (!email && !namePart) return record.id || Math.random().toString(36);
  return `${email}|${namePart}`;
}

export function hasValidLoanAmount(record) {
  return record?.loanAmountNum != null && record.loanAmountNum > 0;
}

/** Keep the most recent submission; tie-break on higher requested amount. */
export function dedupeRecordsByName(records) {
  const map = new Map();

  for (const record of records) {
    const key = buildDedupeKey(record);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, record);
      continue;
    }

    const existingTime = existing.submittedAt ? new Date(existing.submittedAt).getTime() : 0;
    const candidateTime = record.submittedAt ? new Date(record.submittedAt).getTime() : 0;
    const existingAmount = existing.loanAmountNum || 0;
    const candidateAmount = record.loanAmountNum || 0;

    const keepCandidate =
      candidateTime > existingTime ||
      (candidateTime === existingTime && candidateAmount > existingAmount);

    if (keepCandidate) map.set(key, record);
  }

  return Array.from(map.values());
}

export function filterEmptyAmounts(records) {
  return records.filter((r) => hasValidLoanAmount(r));
}

export function countEmptyAmounts(records) {
  return records.filter((r) => !hasValidLoanAmount(r)).length;
}

export function craRevenueBucket(revenue) {
  const rev = String(revenue || '').toLowerCase().trim();
  if (!rev) return 'Unknown';
  if (rev.includes('less than $100')) return '≤ $250K';
  if (rev.includes('$100,000 - $250')) return '$250K – $1M';
  if (rev.includes('$250,000 - $500')) return '$250K – $1M';
  if (rev === '1000000') return '$250K – $1M';
  if (rev.includes('$1 million - $5') || rev.includes('more than $5')) return '> $1M';
  return 'Unknown';
}

export function craLoanSizeBucket(amount) {
  const n = typeof amount === 'number' ? amount : parseLoanAmount(amount);
  if (n == null) return 'Unknown';
  if (n <= 100_000) return '≤ $100K';
  if (n <= 250_000) return '$100K – $250K';
  return '> $250K';
}

export function lmiProxyLabel(level) {
  switch (level) {
    case 'likely_lmi':
      return 'Likely LMI area';
    case 'mixed':
      return 'Mixed income area';
    case 'unlikely_lmi':
      return 'Unlikely LMI area';
    default:
      return 'Unknown';
  }
}

export function getLmiProxy(city, state) {
  return LMI_PROXY_BY_CITY[cityStateKey(city, state)] || 'unknown';
}

export function normalizeApplication(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const loanAmountNum = parseLoanAmount(raw.loanAmount);

  return {
    id: String(raw.loanApplicationId || raw.id || ''),
    submittedAt: raw.submittedAt || raw.createdAt || null,
    loanAmountNum,
    loanAmountLabel: raw.loanAmount != null && raw.loanAmount !== '' ? String(raw.loanAmount) : '—',
    loanPurpose: raw.loanPurpose || '—',
    businessName: raw.businessName || '—',
    businessType: raw.businessType || '—',
    businessIndustry: raw.businessIndustry || '—',
    organizationType: raw.organizationType || '—',
    revenue: raw.revenue || '',
    revenueBucket: craRevenueBucket(raw.revenue),
    loanSizeBucket: craLoanSizeBucket(loanAmountNum),
    city: normalizeCity(raw.city),
    state: normalizeState(raw.state),
    zipCode: String(raw.zipCode || '').trim(),
    streetAddress: raw.streetAddress || '',
    contactName: [raw.firstName, raw.lastName].filter(Boolean).join(' ').trim() || '—',
    email: raw.email || '—',
    timeInBusiness: raw.timeInBusiness || '—',
    lmiProxy: getLmiProxy(raw.city, raw.state),
    faithAlignedPipeline: true,
    raw,
  };
}

export function parseReportPayload(json) {
  const root = json?.data !== undefined ? json.data : json;
  let rows = Array.isArray(root) ? root : root?.items || root?.loanApplications || [];
  if (!Array.isArray(rows)) rows = [];
  return rows.map(normalizeApplication).filter(Boolean).filter((r) => !isTestRecord(r.raw));
}

export function countBy(items, keyFn) {
  const map = new Map();
  for (const item of items) {
    const key = keyFn(item);
    if (!key || key === '—' || key === 'Unknown') continue;
    map.set(key, (map.get(key) || 0) + 1);
  }
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, count }));
}

export function sumBy(items, keyFn) {
  const map = new Map();
  for (const item of items) {
    const key = keyFn(item);
    if (!key) continue;
    map.set(key, (map.get(key) || 0) + (item.loanAmountNum || 0));
  }
  return Array.from(map.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, total]) => ({ label, total }));
}

export function buildCityBreakdown(records) {
  const map = new Map();
  for (const r of records) {
    if (!r.city || !r.state) continue;
    const key = `${r.city}, ${r.state}`;
    const entry = map.get(key) || {
      city: r.city,
      state: r.state,
      count: 0,
      requestedTotal: 0,
      zips: new Set(),
      lmiProxy: r.lmiProxy,
    };
    entry.count += 1;
    entry.requestedTotal += r.loanAmountNum || 0;
    if (r.zipCode) entry.zips.add(r.zipCode);
    map.set(key, entry);
  }
  return Array.from(map.values())
    .sort((a, b) => b.count - a.count)
    .map((e) => ({
      ...e,
      zips: Array.from(e.zips).sort().join(', '),
      lmiLabel: lmiProxyLabel(e.lmiProxy),
    }));
}

export function filterByDateRange(records, startDate, endDate) {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  if (end) end.setHours(23, 59, 59, 999);

  return records.filter((r) => {
    if (!r.submittedAt) return true;
    const d = new Date(r.submittedAt);
    if (Number.isNaN(d.getTime())) return true;
    if (start && d < start) return false;
    if (end && d > end) return false;
    return true;
  });
}

/** Drop applications with a parsed amount below the minimum. */
export function filterByMinLoanAmount(records, minAmount = MIN_REPORT_LOAN_AMOUNT) {
  if (minAmount == null || minAmount <= 0) return records;
  return records.filter((r) => r.loanAmountNum != null && r.loanAmountNum >= minAmount);
}

export function countBelowMinLoanAmount(records, minAmount = MIN_REPORT_LOAN_AMOUNT) {
  if (minAmount == null || minAmount <= 0) return 0;
  return records.filter(
    (r) => r.loanAmountNum != null && r.loanAmountNum < minAmount
  ).length;
}

export function cleanPipelineRecords(records, options = {}) {
  let current = records;
  const stats = {
    excludedEmptyAmountCount: 0,
    excludedSmallLoanCount: 0,
    excludedDuplicateCount: 0,
  };

  if (options.excludeEmptyAmount !== false) {
    const next = filterEmptyAmounts(current);
    stats.excludedEmptyAmountCount = current.length - next.length;
    current = next;
  }

  const minLoanAmount =
    options.minLoanAmount != null ? options.minLoanAmount : MIN_REPORT_LOAN_AMOUNT;
  if (options.excludeBelowMinLoan !== false) {
    const next = filterByMinLoanAmount(current, minLoanAmount);
    stats.excludedSmallLoanCount = current.length - next.length;
    current = next;
  }

  if (options.dedupeByName !== false) {
    const before = current.length;
    current = dedupeRecordsByName(current);
    stats.excludedDuplicateCount = before - current.length;
  }

  return { records: current, stats, minLoanAmount };
}

export function computeModeledImpact(requestedTotal, { conversionRate = 0.25, fundedRatio = 0.85, jobsPer100k = 0.75 } = {}) {
  const deployed = requestedTotal * conversionRate * fundedRatio;
  const jobs = (deployed / 100_000) * jobsPer100k;
  return {
    conversionRate,
    fundedRatio,
    jobsPer100k,
    deployedCapital: deployed,
    jobsSupported: jobs,
  };
}

export function computePipelineReport(records, options = {}) {
  const filtered = filterByDateRange(records, options.startDate, options.endDate);
  const stateFilter = options.state && options.state !== 'all' ? options.state : null;
  const afterState = stateFilter
    ? filtered.filter((r) => r.state === stateFilter)
    : filtered;

  const minLoanAmount =
    options.minLoanAmount != null ? options.minLoanAmount : MIN_REPORT_LOAN_AMOUNT;
  const excludeBelowMinLoan = options.excludeBelowMinLoan !== false;
  const excludeEmptyAmount = options.excludeEmptyAmount !== false;
  const dedupeByName = options.dedupeByName !== false;

  const { records: scoped, stats } = cleanPipelineRecords(afterState, {
    excludeEmptyAmount,
    excludeBelowMinLoan,
    dedupeByName,
    minLoanAmount,
  });

  const withAmount = scoped.filter((r) => r.loanAmountNum != null);
  const requestedTotal = withAmount.reduce((acc, r) => acc + r.loanAmountNum, 0);
  const avgRequest = withAmount.length ? requestedTotal / withAmount.length : 0;

  const dates = scoped
    .map((r) => r.submittedAt)
    .filter(Boolean)
    .map((d) => new Date(d))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b);

  const lmiCounts = countBy(scoped, (r) => lmiProxyLabel(r.lmiProxy));
  const likelyLmiApps = scoped.filter((r) => r.lmiProxy === 'likely_lmi').length;

  return {
    generatedAt: new Date().toISOString(),
    period: {
      start: options.startDate || (dates[0] ? dates[0].toISOString().slice(0, 10) : null),
      end: options.endDate || (dates.at(-1) ? dates.at(-1).toISOString().slice(0, 10) : null),
    },
    filters: {
      excludeEmptyAmount,
      excludeBelowMinLoan,
      dedupeByName,
      minLoanAmount: excludeBelowMinLoan ? minLoanAmount : null,
      excludedEmptyAmountCount: stats.excludedEmptyAmountCount,
      excludedSmallLoanCount: stats.excludedSmallLoanCount,
      excludedDuplicateCount: stats.excludedDuplicateCount,
      totalExcludedCount:
        stats.excludedEmptyAmountCount +
        stats.excludedSmallLoanCount +
        stats.excludedDuplicateCount,
    },
    summary: {
      applicationCount: scoped.length,
      withAmountCount: withAmount.length,
      requestedTotal,
      avgRequest,
      nonprofitCount: scoped.filter((r) => /non-?profit/i.test(r.organizationType)).length,
      forProfitCount: scoped.filter((r) => /for-?profit/i.test(r.organizationType)).length,
      faithAlignedCount: scoped.length,
      likelyLmiCount: likelyLmiApps,
      stateCount: new Set(scoped.map((r) => r.state).filter(Boolean)).size,
      cityCount: new Set(scoped.map((r) => `${r.city}, ${r.state}`).filter((k) => !k.startsWith(','))).size,
    },
    byState: countBy(scoped, (r) => r.state),
    byStateAmount: sumBy(scoped, (r) => r.state),
    byPurpose: countBy(scoped, (r) => r.loanPurpose),
    byIndustry: countBy(scoped, (r) => r.businessIndustry),
    byRevenueBucket: countBy(scoped, (r) => r.revenueBucket),
    byLoanSizeBucket: countBy(scoped, (r) => r.loanSizeBucket),
    byLmiProxy: lmiCounts,
    byOrgType: countBy(scoped, (r) => r.organizationType),
    cities: buildCityBreakdown(scoped),
    modeledImpact: computeModeledImpact(requestedTotal),
    records: scoped,
  };
}

export function recordsToCsvRows(records) {
  return records.map((r) => ({
    'Application ID': r.id,
    'Submitted Date': r.submittedAt ? r.submittedAt.slice(0, 10) : '',
    'Business Name': r.businessName,
    City: r.city,
    State: r.state,
    'ZIP Code': r.zipCode,
    'LMI Proxy': lmiProxyLabel(r.lmiProxy),
    'Requested Amount': r.loanAmountNum ?? '',
    'Loan Purpose': r.loanPurpose,
    Industry: r.businessIndustry,
    'Organization Type': r.organizationType,
    'Revenue Band': r.revenue,
    'CRA Revenue Bucket': r.revenueBucket,
    'CRA Loan Size Bucket': r.loanSizeBucket,
    'Time in Business': r.timeInBusiness,
    'Contact Name': r.contactName,
    Email: r.email,
    'Faith-Aligned Pipeline': 'Yes',
  }));
}

export function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function formatCurrency(n) {
  if (n == null || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return String(n);
  }
}

export function formatNumber(n, digits = 0) {
  if (n == null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(n);
}
